import { Link } from "react-router-dom";
import { History, ImageOff, Tag, X } from "lucide-react";
import { SafeImg } from "../../components/common/SafeImg";
import { useI18n } from "../../i18n";
import { useCart } from "../../context/CartContext";
import { fmtDate, fmtMoney } from "../../lib/format";
import { QtyStepper } from "../../components/common/QtyStepper";
import { trackPromo } from "./promoTrack";
import { canPieces, unitName, unitPrice } from "../../lib/uom";
import { offerFor, savingPct, useOffers } from "../../lib/offers";

export const PriceBreakdown = ({ item, price, mode = "box" }) => {
  const { t } = useI18n();
  const discs = [price.discount_pct, price.disc2_pct, price.disc3_pct].filter((d) => d > 0);
  if (!discs.length) return null;
  const base = mode === "piece" && item.units_per_box ? price.net_price / item.units_per_box : price.net_price;
  return (
    <p className="mt-1 text-xs text-slate-500" data-testid={`price-breakdown-${item.code}`}>
      {t("x.policy.wholesale")} <span className="line-through">{base < 10 ? `${base.toLocaleString("el-GR", { minimumFractionDigits: 2, maximumFractionDigits: 3 })} €` : fmtMoney(base)}</span>
      {discs.map((d, i) => <span key={i} className="ml-1.5 rounded-full bg-brand/20 px-1.5 py-0.5 font-semibold text-navy">−{String(d).replace(".", ",")}%</span>)}
      {discs.length > 1 && <span className="ml-1.5">{t("x.policy.cascade")}</span>}
    </p>
  );
};

export const PriceLine = ({ item, price, mode = "box", compact = false }) => {
  const { t } = useI18n();
  const offers = useOffers();
  const normal = unitPrice(item, price, mode);
  const o = offerFor(offers, item.code, mode, normal);
  return (
    <div data-testid={`price-${item.code}`}>
      {o && <div className="flex items-center gap-1.5 text-[11px]" data-testid={`offer-price-${item.code}`}><span className="rounded-full bg-vblue px-1.5 py-0.5 font-bold text-white">-{savingPct(o.offer_price, normal)}%</span><span className="text-slate-400 line-through">{fmtMoney(normal)}</span>{o.min_qty > 1 && <span className="text-slate-500">{t("x.offers.fromQty", { n: o.min_qty })}</span>}</div>}
      <div className="flex items-baseline gap-1.5">
        <span className={`${compact ? "text-base" : "text-lg"} font-bold tabular-nums ${o ? "text-vblue-ink" : "text-navy"}`}>{fmtMoney(o ? o.offer_price : normal)}</span>
        <span className="text-xs text-slate-500">/ {t(mode === "piece" ? "x.uom.piece" : "x.uom.box")}</span>
      </div>
      <div className="text-[11px] text-slate-500">{mode === "box" && canPieces(item) && <span data-testid={`piece-price-${item.code}`}>{fmtMoney(unitPrice(item, price, "piece"))} / {t("x.uom.piece")}</span>}{price.source === "customer" && <span className="ml-1.5 font-semibold text-brand">· {t("customer.specialPrice")}</span>}</div>
    </div>
  );
};

export const ProductCard = ({ item, price, last, promo }) => {
  const { t, lang } = useI18n();
  const { qtyOf, setQty, modeOf, setMode } = useCart();
  const photo = item.thumb_url || item.photos?.[0];
  const onQty = (q) => {
    if (promo && !qtyOf(item.code) && q > 0) trackPromo(promo.id, "add_to_cart", item.code);
    setQty(item.code, q, item);
  };
  return (
    <article data-testid={`product-card-${item.code}`} className="flex flex-col overflow-hidden rounded-xl bg-white shadow-[0_1px_3px_rgba(0,32,104,0.06)] transition-shadow duration-200 hover:shadow-lg animate-in fade-in duration-300">
      <Link to={`/app/products/${encodeURIComponent(item.code)}`} data-testid={`product-link-${item.code}`} className="group block">
        <div className="relative h-24 overflow-hidden bg-white sm:h-28">
          {photo ? <SafeImg srcs={[photo, item.detail_url, item.photos?.[0]]} alt={item[`name_${lang}`]} loading="lazy" className="h-full w-full scale-[1.7] object-contain transition-transform duration-500 group-hover:scale-[1.8]" /> : <ImageOff className="absolute inset-0 m-auto h-8 w-8 text-slate-300" />}
          {promo && <span data-testid={`promo-badge-${item.code}`} className="absolute right-1.5 top-1.5 flex items-center gap-1 rounded-full bg-brand px-1.5 py-0.5 text-[10px] font-bold text-navy shadow"><Tag className="h-3 w-3" /> {t("promo.badge")}</span>}
        </div>
        <div className="px-2 pt-1.5">
          <span className="text-[10px] text-slate-500">{item.code}</span>
          <h3 className="line-clamp-2 min-h-[2rem] text-xs font-semibold leading-tight text-navy">{item[`name_${lang}`]}</h3>
          {item.units_per_box > 0 && <p className="text-[10px] text-slate-500">{t("common.unitsPerBox", { n: item.units_per_box, unit: unitName(t, item.unit) })}</p>}
        </div>
      </Link>
      <div className="mt-auto space-y-1.5 p-2">
        {last && (
          <p data-testid={`last-bought-${item.code}`} className="flex items-center gap-1 truncate text-[10px] font-medium text-slate-600">
            <History className="h-3 w-3 shrink-0" /> {t("shop.lastBought", { date: fmtDate(last.date), qty: last.qty_boxes, unit: t(last.uom === "piece" ? "x.uom.piece" : "x.uom.box") })}
          </p>
        )}
        <PriceLine item={item} price={price} mode={modeOf(item.code, item)} compact />
        <QtyStepper size="sm" value={qtyOf(item.code)} onChange={onQty} testId={`qty-${item.code}`} mode={modeOf(item.code, item)} onMode={canPieces(item) ? (m) => setMode(item.code, m) : undefined} />
        {qtyOf(item.code) > 0 && <button type="button" onClick={() => setQty(item.code, 0, item)} data-testid={`remove-${item.code}`} className="mt-1 flex h-8 items-center justify-center gap-1 rounded-full text-xs font-semibold text-rose-600 hover:bg-rose-50"><X className="h-3.5 w-3.5" /> {t("shop.removeFromCart")}</button>}
      </div>
    </article>
  );
};

export const ProductGridSkeleton = () => (
  <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6" data-testid="products-skeleton">
    {Array.from({ length: 12 }).map((_, i) => <div key={i} className="h-60 animate-pulse rounded-xl bg-slate-200/70" />)}
  </div>
);
